import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Palette, Tag, Radio, Eye, EyeOff } from "lucide-react";

// Preset colors for frequency markers
export const MARKER_COLORS = [
  { name: "Verde", value: "#34C759" },
  { name: "Azul", value: "#007AFF" },
  { name: "Rojo", value: "#FF3B30" },
  { name: "Amarillo", value: "#FFCC00" },
  { name: "Naranja", value: "#FF9500" },
  { name: "Púrpura", value: "#AF52DE" },
  { name: "Rosa", value: "#FF2D55" },
  { name: "Cyan", value: "#5AC8FA" },
  { name: "Magenta", value: "#FF00FF" },
  { name: "Blanco", value: "#FFFFFF" },
];

// Default marker display settings
export const DEFAULT_MARKER_SETTINGS = {
  showName: true,
  showFrequency: true,
  showChannel: true,
  lineWidth: 2,
  opacity: 0.9,
};

/**
 * MarkerConfigPopover - Popover for configuring frequency marker colors and visibility
 */
export function MarkerConfigPopover({
  systems,
  markerConfig,
  globalMarkerSettings,
  onUpdateMarkerConfig,
  onSetGlobalMarkerSettings,
  onResetMarkerConfig,
  getMarkerColor,
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-[#AF52DE]/50 text-[#AF52DE] hover:bg-[#AF52DE]/10 text-xs"
          data-testid="marker-config-button-header"
        >
          <Palette className="w-3 h-3 mr-1" />
          Colores
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-96 bg-[#111111] border-white/10 p-0"
        align="end"
      >
        <div className="p-3 border-b border-white/10">
          <h4 className="font-semibold text-white text-sm flex items-center gap-2">
            <Tag className="w-4 h-4 text-[#AF52DE]" />
            Marcadores de Frecuencia
          </h4>
          <p className="text-xs text-[#71717A] mt-1">
            Personaliza colores y etiquetas del espectro
          </p>
        </div>
        
        {/* Global Settings */}
        <div className="p-3 border-b border-white/10 space-y-2">
          <p className="text-xs text-[#A1A1AA] font-medium mb-2">Mostrar en etiquetas:</p>
          <div className="grid grid-cols-3 gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={globalMarkerSettings.showName}
                onCheckedChange={(checked) => 
                  onSetGlobalMarkerSettings(prev => ({ ...prev, showName: checked }))
                }
                className="scale-75"
              />
              <span className="text-xs text-[#A1A1AA]">Nombre</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={globalMarkerSettings.showChannel}
                onCheckedChange={(checked) => 
                  onSetGlobalMarkerSettings(prev => ({ ...prev, showChannel: checked }))
                }
                className="scale-75"
              />
              <span className="text-xs text-[#A1A1AA]">Canal</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={globalMarkerSettings.showFrequency}
                onCheckedChange={(checked) => 
                  onSetGlobalMarkerSettings(prev => ({ ...prev, showFrequency: checked }))
                }
                className="scale-75"
              />
              <span className="text-xs text-[#A1A1AA]">Freq</span>
            </label>
          </div>
        </div>
        
        {/* System List */}
        <div className="max-h-64 overflow-y-auto">
          {systems.length === 0 ? (
            <div className="p-4 text-center">
              <Radio className="w-6 h-6 text-[#52525B] mx-auto mb-2" />
              <p className="text-xs text-[#71717A]">No hay sistemas</p>
            </div>
          ) : (
            systems.slice(0, 20).map((system) => {
              const config = markerConfig[system.id] || {};
              const currentColor = config.color || getMarkerColor(system);
              const isHidden = config.hidden === true;
              
              return (
                <div 
                  key={system.id}
                  className={`p-2 border-b border-white/5 flex items-center gap-2 ${
                    isHidden ? 'opacity-50' : ''
                  }`}
                >
                  {/* Color picker */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className="w-5 h-5 rounded border border-white/20 flex-shrink-0"
                        style={{ backgroundColor: currentColor }}
                        title="Cambiar color"
                      />
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-2 bg-[#1A1A1A] border-white/10" side="left">
                      <div className="grid grid-cols-5 gap-1">
                        {MARKER_COLORS.map(color => (
                          <button
                            key={color.value}
                            onClick={() => onUpdateMarkerConfig(system.id, { color: color.value })}
                            className={`w-5 h-5 rounded border-2 ${
                              currentColor === color.value 
                                ? 'border-white' 
                                : 'border-transparent'
                            }`}
                            style={{ backgroundColor: color.value }}
                            title={color.name}
                          />
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  
                  {/* System info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white truncate">
                      {system.name || `CH ${system.channel}`}
                    </p>
                    <p className="text-[10px] font-mono" style={{ color: currentColor }}>
                      {parseFloat(system.frequency).toFixed(3)} MHz
                    </p>
                  </div>
                  
                  {/* Visibility toggle */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onUpdateMarkerConfig(system.id, { hidden: !isHidden })}
                    className={`h-5 w-5 p-0 ${isHidden ? 'text-[#52525B]' : 'text-[#34C759]'}`}
                    title={isHidden ? 'Mostrar' : 'Ocultar'}
                  >
                    {isHidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </Button>
                </div>
              );
            })
          )}
          {systems.length > 20 && (
            <div className="p-2 text-center text-xs text-[#71717A]">
              +{systems.length - 20} más
            </div>
          )}
        </div>
        
        {/* Reset button */}
        {Object.keys(markerConfig).length > 0 && (
          <div className="p-2 border-t border-white/10">
            <Button
              variant="ghost"
              size="sm"
              onClick={onResetMarkerConfig}
              className="w-full text-xs text-[#71717A] hover:text-white"
            >
              Restablecer colores
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default MarkerConfigPopover;
